import { z } from "zod";
import { createStore } from "zustand/vanilla";

import {
  languageSchema,
  unitLengthSchema,
  unitSlopeSchema,
  unitTemperatureSchema,
} from "../studio/searchParams";

/**
 * planSchema is the user's subscription plan ("free" | "pro"), mirroring the
 * backend Plan enum. Zod-first: the Plan type is derived from it.
 */
export const planSchema = z.enum(["free", "pro"]);

/**
 * Plan is the subscription plan, derived from planSchema.
 */
export type Plan = z.infer<typeof planSchema>;

/**
 * planTierSchema names the three identity states the frontend owns: "free", an
 * "individualPro" subscriber (Ory metadata plan === "pro"), and an
 * "organizationPro" user who inherits pro from an active org membership. This is
 * distinct from planSchema (the effective "free" | "pro" the backend mirrors) —
 * the tier carries WHY the user is pro, which the shell surfaces.
 */
export const planTierSchema = z.enum(["free", "individualPro", "organizationPro"]);

/**
 * PlanTier names which of the three states the user is in, derived from
 * planTierSchema.
 */
export type PlanTier = z.infer<typeof planTierSchema>;

/**
 * preferencesSchema mirrors the user's display preferences: the i18n language that
 * drives the bootstrap and the three unit choices, each drawn from the shared
 * searchParams enum that mirrors its backend value domain. Every field is optional
 * and `.catch(undefined)`, so an absent or out-of-range value in a sparse metadata
 * payload resolves to undefined — the consumer falls back to its own default —
 * rather than failing the whole session parse, and the field set matches the auth
 * leaf's preferences shape so the value object auth hands setSession validates
 * identically here. The web app renders light-only, so there is no user-level theme
 * preference.
 */
export const preferencesSchema = z.object({
  language: languageSchema.optional().catch(undefined),
  length: unitLengthSchema.optional().catch(undefined),
  slope: unitSlopeSchema.optional().catch(undefined),
  temperature: unitTemperatureSchema.optional().catch(undefined),
});

/**
 * Preferences is the user's display preferences, derived from preferencesSchema.
 */
export type Preferences = z.infer<typeof preferencesSchema>;

/**
 * traitsSchema mirrors the Ory identity traits (email + names + newsletter).
 */
export const traitsSchema = z.object({
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  newsletter: z.boolean(),
});

/**
 * Traits are the Ory identity traits, derived from traitsSchema.
 */
export type Traits = z.infer<typeof traitsSchema>;

/**
 * identityMetadataSchema is the parsed metadata_public surfaced as the auth
 * context `metadata` (plan + preferences). Zod-first so the store validates what
 * app code hands it.
 */
export const identityMetadataSchema = z.object({
  plan: planSchema,
  preferences: preferencesSchema,
});

/**
 * IdentityMetadata is the parsed metadata_public, derived from
 * identityMetadataSchema.
 */
export type IdentityMetadata = z.infer<typeof identityMetadataSchema>;

/**
 * identitySessionSchema is the value object the auth session bootstrap produces
 * and app code writes into the store via setSession. It is structurally the
 * IdentitySession returned by @mountayaapp/auth (defined here independently to
 * keep core a leaf that does not import auth). isIndividualPro reflects the
 * unmodified Ory plan; org-inherit promotion is applied later. emailVerified is
 * the auth-produced field name; the store surfaces it as isEmailVerified (the
 * rename happens in setSession, matching the store's is* boolean convention).
 */
export const identitySessionSchema = z.object({
  emailVerified: z.boolean(),
  isIndividualPro: z.boolean(),
  metadata: identityMetadataSchema,
  plan: planSchema,
  preferences: preferencesSchema,
  traits: traitsSchema,
  userId: z.string(),
});

/**
 * IdentitySession is the parsed session value object, derived from
 * identitySessionSchema.
 */
export type IdentitySession = z.infer<typeof identitySessionSchema>;

/**
 * IdentityState is the render-free identity slice. It holds the authenticated
 * principal (userId + the hydrated traits/plan/metadata/preferences), the active
 * organization id, and whether the user is a member of an active organization —
 * client state only, never server state. Auth context fields are null until the
 * SPA session bootstrap hydrates them. isIndividualPro reflects the unmodified
 * Ory metadata plan; isOrganizationMember is set from the org-lookup once the
 * guard resolves it. isEmailVerified reflects whether the Ory identity has a
 * verified email address (verification is non-blocking, so it is false until the
 * user verifies). `plan` is the EFFECTIVE plan recomputed from both sources.
 */
export interface IdentityState {
  isEmailVerified: boolean;
  isIndividualPro: boolean;
  isOrganizationMember: boolean;
  metadata: IdentityMetadata | null;
  organizationId: string | null;
  plan: Plan | null;
  preferences: Preferences | null;
  traits: Traits | null;
  userId: string | null;
}

/**
 * IdentityActions are the mutation entry points for the identity slice. Per the
 * one-mutation-surface invariant, app components reach these only through the
 * tool bus — never by importing this store directly. setSession hydrates the
 * whole auth context from the bootstrap value object; reset clears it on logout.
 */
export interface IdentityActions {
  reset: () => void;
  setMembership: (isOrganizationMember: boolean) => void;
  setOrganization: (organizationId: string | null) => void;
  setPreferences: (partial: Partial<Preferences>) => void;
  setSession: (session: IdentitySession) => void;
  setUser: (userId: string | null) => void;
}

export type IdentityStore = IdentityActions & IdentityState;

const initialState: IdentityState = {
  isEmailVerified: false,
  isIndividualPro: false,
  isOrganizationMember: false,
  metadata: null,
  organizationId: null,
  plan: null,
  preferences: null,
  traits: null,
  userId: null,
};

/**
 * createIdentityStore builds a vanilla (render-free) Zustand store. The React
 * provider that binds it lives in @mountayaapp/theme (a sanctioned JSX
 * carve-out), keeping this package free of react-dom.
 */
export const createIdentityStore = () =>
  createStore<IdentityStore>((set) => ({
    ...initialState,
    reset: () => set(initialState),
    // Recompute the EFFECTIVE plan from both the individual subscription and the
    // org membership: a free user who joins an active org inherits pro. The
    // frontend owns this promotion (it is not read back from the backend).
    setMembership: (isOrganizationMember) =>
      set((state) => ({
        isOrganizationMember,
        plan: state.isIndividualPro || isOrganizationMember ? "pro" : "free",
      })),
    setOrganization: (organizationId) => set({ organizationId }),
    // Merge the partial into the current preferences for client-side display
    // updates. When no preferences are hydrated yet, start from an empty base so a
    // partial update produces a valid value object; fields the partial omits stay
    // absent, and each consumer falls back to its own default.
    setPreferences: (partial) =>
      set((state) => ({
        preferences: { ...(state.preferences ?? emptyPreferences()), ...partial },
      })),
    setSession: (session) =>
      set({
        isEmailVerified: session.emailVerified,
        isIndividualPro: session.isIndividualPro,
        metadata: session.metadata,
        plan: session.plan,
        preferences: session.preferences,
        traits: session.traits,
        userId: session.userId,
      }),
    setUser: (userId) => set({ userId }),
  }));

export type IdentityStoreApi = ReturnType<typeof createIdentityStore>;

/**
 * emptyPreferences is the empty Preferences value object, used to seed the merge in
 * setPreferences when no preferences are hydrated yet. Every field is optional, so
 * the parse yields an object with no fields set that a partial update merges over.
 */
const emptyPreferences = (): Preferences => preferencesSchema.parse({});

/**
 * resolvePlanTier names which of the three tier states the user is in. The
 * individual subscription takes precedence over org-inherited pro, so a
 * subscriber who is also an org member reads as "individualPro". Render-free and
 * pure: components select isIndividualPro + isOrganizationMember and pass them
 * here rather than branching inline.
 */
export const resolvePlanTier = (
  state: Pick<IdentityState, "isIndividualPro" | "isOrganizationMember">,
): PlanTier => {
  if (state.isIndividualPro) {
    return "individualPro";
  }

  if (state.isOrganizationMember) {
    return "organizationPro";
  }

  return "free";
};
